/**
 * Lead Builder: demand clusters (TRUS-820, D6). Platform team only.
 *
 * Ranked ZIP clusters for one service type: member requests short of
 * partners, committed volume vs listed capacity, modeled headroom and current
 * supply. "Create campaign" builds a Lead Builder list of the cluster's
 * directory organizations that have a decision maker on file, then creates a
 * draft cold-outreach campaign from it. Nothing is sent until someone starts
 * the campaign. Empty values stay empty: a dash means "no data", never zero.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, MapPin, Megaphone } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { config as productConfig } from '@/src/config';
import { coldOutreachApi } from '../../../shared/services/platformApi';
import { formatClusterNumber } from './demandClusterFormat';

const API_BASE = (productConfig.weaveinApi.baseUrl ?? '').trim().replace(/\/$/, '');

export interface DemandCluster {
  cluster_id: string;
  rank: number;
  county_fips: string | null;
  county_name: string | null;
  zips: string[];
  modeled: { low: number; mid: number; high: number } | null;
  headroom: { mid: number | null; basis: string };
  committed: { per_month: number; sources: string[] } | null;
  requests: number;
  short_requests: number;
  supply: {
    partner_listings: number;
    listed_capacity_per_month: number | null;
    listings_without_cap: number;
    directory_orgs: number | null;
    directory_basis: string | null;
  };
  why: string;
}

function headers(): HeadersInit {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('auth_token');
  if (token) h.Authorization = `Bearer ${token}`;
  const cbo = localStorage.getItem('active_cbo_id');
  if (cbo) h['X-CBO-ID'] = cbo;
  return h;
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers: headers() });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(json.detail || `HTTP ${res.status}`) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  return json as T;
}

export function DemandClustersTab() {
  const { t } = useAppTranslation();
  const [serviceTypes, setServiceTypes] = useState<{ service_type: string; label: string }[]>([]);
  const [service, setService] = useState('');
  const [clusters, setClusters] = useState<DemandCluster[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, string>>({});

  useEffect(() => {
    call<{ service_types: { service_type: string; label: string }[] }>('/api/admin/demand/service-types')
      .then((r) => {
        setServiceTypes(r.service_types);
        if (r.service_types[0]) setService(r.service_types[0].service_type);
      })
      .catch((e) =>
        setError(e.status === 403 ? t('demandClusters.platformOnly', 'Demand clusters are for the platform team.') : e.message),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    if (!service) return;
    setLoading(true);
    setError(null);
    try {
      const r = await call<{ clusters: DemandCluster[] }>(`/api/admin/demand/clusters?service_type=${encodeURIComponent(service)}`);
      setClusters(r.clusters);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [service]);

  useEffect(() => {
    load();
  }, [load]);

  const createCampaign = async (c: DemandCluster) => {
    const cbo = Number(localStorage.getItem('active_cbo_id') || '');
    if (!cbo) {
      setResults((r) => ({ ...r, [c.cluster_id]: t('demandClusters.noOrg', 'Pick your organization first.') }));
      return;
    }
    const label = `${c.county_name || c.county_fips || ''} ${c.zips[0]?.slice(0, 3) ?? ''}xx`.trim();
    setBusy(c.cluster_id);
    try {
      const list = await call<{
        list_id: string | null;
        orgs_found: number;
        orgs_with_decision_makers: number;
        orgs_needing_contact_discovery: number;
      }>('/api/admin/demand/clusters/outreach-list', {
        method: 'POST',
        body: JSON.stringify({ service_type: service, zips: c.zips, cbo_id: cbo, label }),
      });
      if (!list.list_id) {
        setResults((r) => ({
          ...r,
          [c.cluster_id]: t(
            'demandClusters.noDecisionMakers',
            '{{found}} organizations found, none with a decision maker yet. They are in Matched Companies for contact discovery.',
            { found: list.orgs_found },
          ),
        }));
        return;
      }
      const resp = await coldOutreachApi.createCampaign({
        name: t('demandClusters.campaignName', 'Demand cluster {{label}} ({{service}})', { label, service }),
        source_list_id: list.list_id,
        source_list_type: 'lead_builder',
      });
      setResults((r) => ({
        ...r,
        [c.cluster_id]: resp.success
          ? t('demandClusters.created', 'Draft campaign created with {{n}} organizations. {{rest}} more need contact discovery.', {
              n: list.orgs_with_decision_makers,
              rest: list.orgs_needing_contact_discovery,
            })
          : t('demandClusters.listOnly', 'Outreach list created; the campaign could not be created: {{error}}', {
              error: resp.error?.message || '',
            }),
      }));
    } catch (e: any) {
      setResults((r) => ({ ...r, [c.cluster_id]: e.message }));
    } finally {
      setBusy(null);
    }
  };

  const n = (v: number | null | undefined) => formatClusterNumber(v);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <span className="block font-medium text-gray-700">{t('demandClusters.service', 'Service')}</span>
          <select
            className="mt-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
            value={service}
            onChange={(e) => setService(e.target.value)}
          >
            {serviceTypes.map((s) => (
              <option key={s.service_type} value={s.service_type}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
        <p className="max-w-2xl text-xs text-gray-500">
          {t(
            'demandClusters.intro',
            'Ranked by people already waiting, then committed volume above listed capacity, then modeled headroom per organization. A dash means no data.',
          )}
        </p>
      </div>
      {error && <p className="text-sm text-red-700">{error}</p>}
      {loading ? (
        <p className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> {t('demandClusters.loading', 'Loading clusters…')}
        </p>
      ) : (
        !error && clusters.length === 0 && <p className="text-sm text-gray-500">{t('demandClusters.empty', 'No clusters with demand data for this service yet.')}</p>
      )}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs uppercase tracking-wide text-gray-500">
              <th className="px-2 py-2">#</th>
              <th className="px-2 py-2">{t('demandClusters.area', 'Area')}</th>
              <th className="px-2 py-2">{t('demandClusters.waiting', 'Requests (short)')}</th>
              <th className="px-2 py-2">{t('demandClusters.committed', 'Committed / month')}</th>
              <th className="px-2 py-2">{t('demandClusters.modeled', 'Modeled range')}</th>
              <th className="px-2 py-2">{t('demandClusters.headroom', 'Headroom')}</th>
              <th className="px-2 py-2">{t('demandClusters.supply', 'Supply')}</th>
              <th className="px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {clusters.map((c) => (
              <tr key={c.cluster_id} className="border-b align-top">
                <td className="px-2 py-2 text-gray-500">{c.rank}</td>
                <td className="px-2 py-2">
                  <div className="flex items-center gap-1 font-medium text-gray-900">
                    <MapPin className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
                    {c.county_name || c.county_fips || t('demandClusters.unknownCounty', 'Unknown county')}
                  </div>
                  <div className="text-xs text-gray-500">{c.zips.join(', ')}</div>
                  <div className="mt-1 text-xs text-gray-400">{c.why}</div>
                </td>
                <td className="px-2 py-2">
                  {c.requests} ({c.short_requests})
                </td>
                <td className="px-2 py-2">
                  {c.committed ? (
                    <>
                      {n(c.committed.per_month)}
                      <div className="text-xs text-gray-500">{c.committed.sources.join(', ')}</div>
                    </>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="px-2 py-2">{c.modeled ? `${n(c.modeled.low)}–${n(c.modeled.high)}` : '—'}</td>
                <td className="px-2 py-2" title={c.headroom.basis}>
                  {n(c.headroom.mid)}
                </td>
                <td className="px-2 py-2 text-xs text-gray-600">
                  {t('demandClusters.listings', '{{n}} partner listings', { n: c.supply.partner_listings })}
                  {c.supply.listed_capacity_per_month !== null &&
                    ` · ${t('demandClusters.capacity', '{{n}}/month listed', { n: n(c.supply.listed_capacity_per_month) })}`}
                  <br />
                  {c.supply.directory_orgs === null
                    ? t('demandClusters.directoryUnknown', 'Directory: not looked up')
                    : t('demandClusters.directory', 'Directory: {{n}} organizations', { n: c.supply.directory_orgs })}
                </td>
                <td className="px-2 py-2">
                  <button
                    type="button"
                    onClick={() => createCampaign(c)}
                    disabled={busy !== null}
                    className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {busy === c.cluster_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Megaphone className="h-3.5 w-3.5" />}
                    {t('demandClusters.createCampaign', 'Create campaign')}
                  </button>
                  {results[c.cluster_id] && <p className="mt-1 max-w-xs text-xs text-gray-600">{results[c.cluster_id]}</p>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
